/* =====================================================================
 * DocxTable — 테이블 렌더링 (셀 병합, 테두리, 배경색)
 * ===================================================================== */

import React from 'react';
import type {
  DocxTable as DocxTableType,
  DocxDocument,
  DocxBorder,
} from '@auw/workspace-utils';
import { DocxParagraphComponent } from './DocxParagraph';

interface DocxTableComponentProps {
  table: DocxTableType;
  document: DocxDocument;
}

function borderToCSS(border?: DocxBorder): string {
  if (!border || border.style === 'none' || border.style === 'nil') return 'none';
  const width = border.size ? `${Math.max(border.size / 8, 1)}px` : '1px';
  const color = border.color || '#000000';
  const style = border.style === 'dashed' ? 'dashed'
    : border.style === 'dotted' ? 'dotted'
    : border.style === 'double' ? 'double'
    : 'solid';
  return `${width} ${style} ${color}`;
}

export const DocxTableComponent: React.FC<DocxTableComponentProps> = ({
  table,
  document: doc,
}) => {
  const rowCount = table.rows.length;
  const hasTblBorders = !!table.borders;
  const FALLBACK_BORDER = '1px solid #e0e0e0';

  function resolveBorder(css: string): string {
    if (css !== 'none') return css;
    return hasTblBorders ? 'none' : FALLBACK_BORDER;
  }

  return (
    <table
      data-block-id={table.id}
      data-block-type="table"
      style={{
        borderCollapse: 'collapse',
        width: '100%',
        margin: '8px 0',
        tableLayout: 'auto',
      }}
    >
      <tbody>
        {table.rows.map((row, rowIdx) => (
          <tr key={rowIdx}>
            {row.cells.map((cell, cellIdx) => {
              const colCount = row.cells.length;
              const isLastRow = rowIdx === rowCount - 1;
              const isLastCol = cellIdx === colCount - 1;
              const tb = table.borders;

              const topBorder = cell.borders?.top ?? (rowIdx > 0 ? tb?.insideH : tb?.top);
              const bottomBorder = cell.borders?.bottom ?? (isLastRow ? tb?.bottom : tb?.insideH);
              const leftBorder = cell.borders?.left ?? (cellIdx > 0 ? tb?.insideV : tb?.left);
              const rightBorder = cell.borders?.right ?? (isLastCol ? tb?.right : tb?.insideV);

              const topCss = borderToCSS(topBorder);
              const bottomCss = borderToCSS(bottomBorder);
              const leftCss = borderToCSS(leftBorder);
              const rightCss = borderToCSS(rightBorder);

              return (
                <td
                  key={cellIdx}
                  colSpan={cell.colSpan && cell.colSpan > 1 ? cell.colSpan : undefined}
                  rowSpan={cell.rowSpan && cell.rowSpan > 1 ? cell.rowSpan : undefined}
                  style={{
                    padding: '4px 6px',
                    verticalAlign: 'top',
                    backgroundColor: cell.shading || undefined,
                    width: cell.width ? `${cell.width}px` : undefined,
                    borderTop: resolveBorder(topCss),
                    borderBottom: resolveBorder(bottomCss),
                    borderLeft: resolveBorder(leftCss),
                    borderRight: resolveBorder(rightCss),
                  }}
                >
                  {cell.paragraphs.map((para) => (
                    <DocxParagraphComponent
                      key={para.id}
                      paragraph={para}
                      document={doc}
                      inTable
                    />
                  ))}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
